// FIX: Corrected a malformed import statement that was causing multiple compilation errors.
import React from 'react';
import type { Testimonial, OfferStackItem, MechanismStep, ContentBlock, FAQ } from '../types';
import AnimatedSection from '../components/ui/AnimatedSection';
import { Star } from 'react-feather';
import CTABand from '../components/funnel/CTABand';
import Seo from '../components/shared/Seo';
import FunnelHero from '../components/funnel/FunnelHero';
import AuthorityBurst from '../components/funnel/AuthorityBurst';
import EpiphanyBridgeContent from '../components/funnel/EpiphanyBridgeContent';
import MechanismDiagram from '../components/funnel/MechanismDiagram';
import OfferStack from '../components/funnel/OfferStack';
import FaqSection from '../components/blog/FaqSection';
import { generateFaqSchema } from '../lib/schema';
import FlippableCard from '../components/ui/FlippableCard';
import { useData } from '../context/DataContext';
import { useTranslation } from 'react-i18next';

const ReviewsPage: React.FC = () => {
  const { t } = useTranslation('common');
  const { testimonials: testimonialsData, caseResults: caseResultsData, firmStats, commonOfferStack, commonRiskReversal } = useData();

  const epiphanyBridgeContent = t('reviewsPage.epiphanyBridgeContent', { returnObjects: true }) as ContentBlock[];
  const mechanism = t('reviewsPage.mechanism', { returnObjects: true }) as { title: string; subtitle: string; steps: MechanismStep[] };
  const offerStack: OfferStackItem[] = commonOfferStack;
  const riskReversal = commonRiskReversal;
  const faqs = t('reviewsPage.faqs', { returnObjects: true }) as FAQ[];

  const faqSchema = generateFaqSchema(faqs);
  const pageSchemas = [faqSchema].filter(Boolean);


  const renderReview = (testimonial: Testimonial, index: number) => {
    const pairedResult = caseResultsData.length > 0 ? caseResultsData[index % caseResultsData.length] : null;
    return (
        <FlippableCard
          key={index}
          frontContent={
            <div className="flex h-full flex-col justify-center rounded-3xl border border-white/10 bg-white/5 p-8 shadow-xl glass-effect">
              <p className="text-xl leading-relaxed text-white text-center">“{testimonial.quote}”</p>
            </div>
          }
          backContent={
            <div className="flex h-full flex-col items-center justify-center rounded-3xl border border-white/10 bg-black/80 p-8 shadow-xl glass-effect text-center">
              <div className="text-3xl font-bold gold-gradient-text">{pairedResult?.amount || 'Confidential'}</div>
              <div className="mt-2 text-md text-white/80">{pairedResult?.caseType || 'Successful Settlement'}</div>
            </div>
          }
        />
    );
  };

  return (
    <>
      <Seo
          title={t('reviewsPage.seoTitle')}
          description={t('reviewsPage.seoDescription')}
          schema={pageSchemas}
      />
      <div>
        <FunnelHero
          headline={t('reviewsPage.heroHeadline')}
          subheadline={t('reviewsPage.heroSubheadline')}
          imageUrl="https://images.pexels.com/photos/8112172/pexels-photo-8112172.jpeg?auto=compress&cs=tinysrgb&w=1600"
        />
        
        <AuthorityBurst stats={firmStats} />

        <div className="py-24 bg-brand-dark">
          <AnimatedSection>
              <EpiphanyBridgeContent content={epiphanyBridgeContent} />
          </AnimatedSection>
        </div>
        
        <MechanismDiagram mechanism={mechanism} />
        
        <AnimatedSection className="py-24">
          <div className="mx-auto max-w-4xl px-6">
              <div className="text-center mb-16">
                  <h2 className="text-4xl md:text-5xl font-serif font-bold">{t('reviewsPage.reviewsTitle')}</h2>
                  <p className="mt-4 text-lg text-white/80">{t('reviewsPage.reviewsSubtitle')}</p>
              </div>
              {testimonialsData.length > 0 && (
                <div className="grid grid-cols-1 gap-8">
                    {testimonialsData.map(renderReview)}
                </div>
              )}
          </div>
        </AnimatedSection>

        <OfferStack offerStack={offerStack} />

        <div className="py-24 bg-white/5">
            <FaqSection 
              faqs={faqs}
              title={t('reviewsPage.faqTitle')}
            />
         </div>

        <AnimatedSection className="py-24">
           <div className="mx-auto max-w-4xl px-6 text-center">
              <h2 className="text-4xl md:text-5xl font-serif font-bold">{riskReversal.title}</h2>
              <p className="mt-6 text-lg text-white/80">{riskReversal.description}</p>
           </div>
        </AnimatedSection>
        
        <CTABand
            headline={t('reviewsPage.ctaBandHeadline')}
            subheadline={t('reviewsPage.ctaBandSubheadline')}
            buttonText={t('reviewsPage.ctaBandButton')}
            buttonLink="/contact"
            isPrimary={true}
        />

      </div>
    </>
  );
};

export default ReviewsPage;